import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../../ui/LoadingSpinner'
import type { IwmRootMappingPreview } from '../../../utils/pqr/iwmRootMappingPreview'
import type { FetchRootBugTypeAggregateResult } from '../../../utils/weldTelemetryClient'
import {
  formatRootAggCell,
  IWM_ROOT_DIALOG_PARAM_KEYS,
  mapsToBugType,
  rootAggTripleForParam,
  rootDialogParamHeader
} from '../../../utils/pqr/iwmRootAggregateDisplay'
import {
  IWM_ROOT_MODE_FLIP_ARROW,
  IWM_ROOT_MODE_FLIP_LABEL,
  IWM_ROOT_MODE_KEEP_ARROW,
  IWM_ROOT_MODE_KEEP_LABEL
} from '../../../utils/pqr/swapIwmBugType'

interface Props {
  open: boolean
  flipEnabled: boolean
  preview: IwmRootMappingPreview | null
  aggregate: FetchRootBugTypeAggregateResult | null
  loading?: boolean
  saving?: boolean
  preheatUnit?: string
  onSave: (flipEnabled: boolean) => void | Promise<void>
  onCancel: () => void
}

const AGGS = ['min', 'avg', 'max'] as const

/**
 * Wide dialog: Root CW / Root CCW WP-style stats + As logged / Swap.
 */
export default function IwmRootMappingDialog({
  open,
  flipEnabled,
  preview,
  aggregate,
  loading = false,
  saving = false,
  preheatUnit,
  onSave,
  onCancel
}: Props) {
  const [draftSwap, setDraftSwap] = useState(flipEnabled)

  useEffect(() => {
    if (open) setDraftSwap(flipEnabled)
  }, [open, flipEnabled])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, saving, onCancel])

  if (!open) return null

  const canSwap = preview?.canFlipRoot ?? false
  const effectiveSwap = canSwap ? draftSwap : false
  const cwRow = aggregate?.rows.find((r) => r.bugType === 'CW')
  const ccwRow = aggregate?.rows.find((r) => r.bugType === 'CCW')
  const hasCw = cwRow?.present || (preview?.rootBugTypes.includes('CW') ?? false)
  const hasCcw = ccwRow?.present || (preview?.rootBugTypes.includes('CCW') ?? false)
  const disableNote =
    !canSwap && flipEnabled
      ? 'No Root CW/CCW data on this weld. Saving will switch to As logged.'
      : preview?.disableReason ?? null

  const content = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-3"
      style={{ zIndex: 10000 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="iwm-root-mapping-title"
      data-testid="iwm-root-mapping-dialog"
    >
      <div
        className="relative flex max-h-[94vh] w-full max-w-[min(98vw,1280px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#E2E5EA] px-5 py-3.5">
          <h2
            id="iwm-root-mapping-title"
            className="text-lg font-semibold text-gray-900"
          >
            IWM Root mapping
          </h2>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            onClick={onCancel}
            disabled={saving}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto px-5 py-4">
          {loading || !preview ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-heritage-grey">
              <LoadingSpinner size="sm" />
              Loading…
            </div>
          ) : (
            <>
              <div
                className="overflow-x-auto rounded-lg border border-[#E2E5EA]"
                data-testid="iwm-root-mapping-table"
              >
                <table className="min-w-full border-separate border-spacing-0 text-left">
                  <thead className="bg-gray-50">
                    <tr className="h-9">
                      <th
                        rowSpan={2}
                        className="sticky left-0 z-20 border-b border-[#E2E5EA] bg-gray-50 px-3 text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                      >
                        Pass
                      </th>
                      <th
                        rowSpan={2}
                        className="border-b border-l border-[#E2E5EA] px-3 text-center text-xs font-medium text-gray-600"
                      >
                        Present
                      </th>
                      <th
                        rowSpan={2}
                        className="border-b border-l border-[#E2E5EA] px-3 text-center text-xs font-medium text-gray-600"
                      >
                        Maps to
                      </th>
                      {IWM_ROOT_DIALOG_PARAM_KEYS.map((key) => (
                        <th
                          key={key}
                          colSpan={3}
                          className="border-b border-l border-[#E2E5EA] px-2 text-center text-xs font-medium text-gray-600"
                        >
                          {rootDialogParamHeader(key, preheatUnit)}
                        </th>
                      ))}
                    </tr>
                    <tr className="h-8">
                      {IWM_ROOT_DIALOG_PARAM_KEYS.map((key) =>
                        AGGS.map((agg, i) => (
                          <th
                            key={`${key}-${agg}`}
                            className={`border-b border-[#E2E5EA] px-2 text-center text-[10px] font-medium uppercase tracking-wide text-gray-500 ${
                              i === 0 ? 'border-l' : ''
                            }`}
                          >
                            {agg}
                          </th>
                        ))
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      [
                        { label: 'Root CW', logged: 'CW' as const, present: hasCw, row: cwRow },
                        { label: 'Root CCW', logged: 'CCW' as const, present: hasCcw, row: ccwRow }
                      ] as const
                    ).map((entry) => (
                      <tr
                        key={entry.label}
                        className="h-[52px] bg-white"
                        data-testid={`iwm-root-row-${entry.logged.toLowerCase()}`}
                      >
                        <td className="sticky left-0 z-10 border-b border-gray-200 bg-white px-3 text-xs font-semibold text-text-grey shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                          {entry.label}
                        </td>
                        <td
                          className="border-b border-l border-gray-200 px-3 text-center text-xs text-gray-600"
                          data-testid={`iwm-root-has-${entry.logged.toLowerCase()}`}
                        >
                          {entry.present ? 'Yes' : '—'}
                        </td>
                        <td
                          className="border-b border-l border-gray-200 px-3 text-center text-xs font-semibold text-text-grey"
                          data-testid={`iwm-root-maps-${entry.logged.toLowerCase()}`}
                        >
                          {mapsToBugType(entry.logged, effectiveSwap)}
                        </td>
                        {IWM_ROOT_DIALOG_PARAM_KEYS.map((key) => {
                          const triple = rootAggTripleForParam(entry.row, key)
                          return AGGS.map((agg, i) => (
                            <td
                              key={`${entry.label}-${key}-${agg}`}
                              className={`border-b border-gray-200 px-2 text-center text-xs tabular-nums text-gray-600 ${
                                i === 0 ? 'border-l border-[#E2E5EA]' : 'border-l border-[#EEF0F3]'
                              }`}
                            >
                              {formatRootAggCell(triple[agg])}
                            </td>
                          ))
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <fieldset className="space-y-2">
                <legend className="sr-only">Mapping mode</legend>
                <label
                  className={`flex cursor-pointer gap-3 rounded-md border px-3 py-2.5 ${
                    !effectiveSwap
                      ? 'border-[#C9CDD4] bg-page-bg'
                      : 'border-[#E2E5EA] bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="iwm-root-mode"
                    data-testid="iwm-root-mode-keep"
                    checked={!effectiveSwap}
                    disabled={saving}
                    onChange={() => setDraftSwap(false)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-text-grey">
                      {IWM_ROOT_MODE_KEEP_LABEL}
                    </span>
                    <span className="mt-0.5 block text-xs font-medium text-heritage-grey">
                      {IWM_ROOT_MODE_KEEP_ARROW}
                    </span>
                  </span>
                </label>
                <label
                  className={`flex gap-3 rounded-md border px-3 py-2.5 ${
                    !canSwap
                      ? 'cursor-not-allowed border-[#E2E5EA] bg-page-bg opacity-60'
                      : effectiveSwap
                        ? 'cursor-pointer border-[#C9CDD4] bg-page-bg'
                        : 'cursor-pointer border-[#E2E5EA] bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="iwm-root-mode"
                    data-testid="iwm-root-mode-flip"
                    checked={effectiveSwap}
                    disabled={saving || !canSwap}
                    onChange={() => setDraftSwap(true)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-text-grey">
                      {IWM_ROOT_MODE_FLIP_LABEL}
                    </span>
                    <span className="mt-0.5 block text-xs font-medium text-heritage-grey">
                      {IWM_ROOT_MODE_FLIP_ARROW}
                    </span>
                    {!canSwap && disableNote ? (
                      <span
                        className="mt-1 block text-xs text-error-700"
                        data-testid="iwm-root-disable-reason"
                      >
                        {disableNote}
                      </span>
                    ) : null}
                  </span>
                </label>
              </fieldset>
            </>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-[#E2E5EA] px-5 py-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="iwm-root-mapping-save"
            disabled={saving || loading || !preview}
            onClick={() => void onSave(effectiveSwap)}
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2 border-white border-r-transparent" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
